import apiClient from '@/lib/api-client'
import type { Team, TeamInvitation, TeamMember, StaffMember } from '@/types'

export const teamApi = {
  async createTeam(data: { name?: string } = {}): Promise<Team> {
    const response = await apiClient.post<{ success: boolean; data: Team }>('/teams', data)
    return response.data.data
  },

  async getMyTeams(): Promise<Team[]> {
    const response = await apiClient.get<{ success: boolean; data: Team[] }>('/teams/mine')
    return response.data.data
  },

  async getTeam(id: string): Promise<Team> {
    const response = await apiClient.get<{ success: boolean; data: Team }>(`/teams/${id}`)
    return response.data.data
  },

  async updateTeam(id: string, data: { name?: string; settings?: Record<string, unknown> }): Promise<Team> {
    const response = await apiClient.patch<{ success: boolean; data: Team }>(`/teams/${id}`, data)
    return response.data.data
  },

  async invite(teamId: string, email: string, role: 'admin' | 'member' = 'member'): Promise<TeamInvitation> {
    const response = await apiClient.post<{ success: boolean; data: TeamInvitation }>(
      `/teams/${teamId}/invitations`,
      { email, role }
    )
    return response.data.data
  },

  async listInvitations(teamId: string): Promise<TeamInvitation[]> {
    const response = await apiClient.get<{ success: boolean; data: TeamInvitation[] }>(
      `/teams/${teamId}/invitations`
    )
    return response.data.data
  },

  async revokeInvitation(teamId: string, invitationId: string): Promise<void> {
    await apiClient.delete(`/teams/${teamId}/invitations/${invitationId}`)
  },

  async acceptInvitation(token: string): Promise<TeamMember> {
    const response = await apiClient.post<{ success: boolean; data: TeamMember }>(
      '/teams/invitations/accept',
      { token }
    )
    return response.data.data
  },

  async updateMemberRole(teamId: string, memberId: string, role: 'admin' | 'member'): Promise<TeamMember> {
    const response = await apiClient.patch<{ success: boolean; data: TeamMember }>(
      `/teams/${teamId}/members/${memberId}`,
      { role }
    )
    return response.data.data
  },

  async removeMember(teamId: string, memberId: string): Promise<void> {
    await apiClient.delete(`/teams/${teamId}/members/${memberId}`)
  },

  async getStaff(teamId: string): Promise<StaffMember[]> {
    const response = await apiClient.get<{ success: boolean; data: StaffMember[] }>(
      `/teams/${teamId}/staff`
    )
    return response.data.data
  },

  async getPublicStaff(username: string, serviceIds: string[] = []): Promise<StaffMember[]> {
    const params = new URLSearchParams();
    serviceIds.forEach((id) => params.append('service_ids[]', id));
    const qs = params.toString();
    const response = await apiClient.get<{ success: boolean; data: StaffMember[] }>(
      `/profiles/${username}/staff${qs ? `?${qs}` : ''}`
    )
    return response.data.data
  },
}
